import { ADMIN_DOOR_WORDING } from '@/lib/admin-labels';
import { Alert, AuthLayout, Button, TextField } from '@pms-digital/design-system';
import type { FormEvent } from 'react';

/**
 * The door of the administration (auth Tahiti §6): the package `AuthLayout`, the login screen of
 * every PMS application - the gray-50 ground, the white card with the official logo, the title
 * and the subtitle, the help line under the card.
 *
 * Two contracts live here, as they did in the card this replaces:
 *
 * - **The title names the DESTINATION**, read from the door catalogue (`lib/admin-nav.ts`), and
 *   the button carries its verb: the e2e specs click it by that name.
 * - **The password toggle is not named « mot de passe »**: `admin-login.spec.ts` fills
 *   `getByLabel(/mot de passe/i)`, which would resolve the field AND the toggle. The package
 *   names its toggle « Afficher le mot de passe » by default, so it is renamed below.
 *
 * `data-testid="porte-carte"` is what the capture script reads to know the door is drawn.
 */
export interface LoginCardProps {
  title: string;
  action: string;
  notice?: string | null;
  submitting?: boolean;
  onSubmit: (email: string, password: string) => void;
}

export function LoginCard({
  title,
  action,
  notice = null,
  submitting = false,
  onSubmit,
}: LoginCardProps) {
  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    /*
      The address is trimmed and the password is NOT. A leading space in an address is a
      copy-paste artefact; a leading space in a password is a character somebody chose.
    */
    onSubmit(String(form.get('adminEmail') ?? '').trim(), String(form.get('adminPassword') ?? ''));
  }

  return (
    <AuthLayout
      title={title}
      subtitle={ADMIN_DOOR_WORDING.subtitle}
      footer={ADMIN_DOOR_WORDING.help}
    >
      <div data-testid="porte-carte" className="flex flex-col gap-6">
        {/*
          An expiry or a refusal is not an error of the operator's: the information tone, never
          the alert one - an operator whose session simply lapsed would otherwise go re-check a
          password that was right.
        */}
        {notice === null ? null : <Alert tone="info">{notice}</Alert>}
        <form onSubmit={submit} className="flex flex-col gap-4">
          <TextField
            id="adminEmail"
            name="adminEmail"
            type="email"
            autoComplete="username"
            label={ADMIN_DOOR_WORDING.emailLabel}
            placeholder={ADMIN_DOOR_WORDING.emailPlaceholder}
            required
          />
          <TextField
            id="adminPassword"
            name="adminPassword"
            type="password"
            autoComplete="current-password"
            label={ADMIN_DOOR_WORDING.passwordLabel}
            passwordToggleLabel={ADMIN_DOOR_WORDING.passwordToggle}
            required
          />
          <Button
            type="submit"
            variant="secondary"
            size="comfortable"
            fullWidth
            loading={submitting}
            loadingLabel={ADMIN_DOOR_WORDING.submitting}
          >
            {action}
          </Button>
        </form>
      </div>
    </AuthLayout>
  );
}
